import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/GlobalStyles.css";
import { memo, useMemo, useState, useRef } from "react";
import {
  Event,
  AccountData,
  DiscussionPost,
  PromoCode,
  TicketV2,
  CustomTicketV2,
  SavedFormQuestion,
  MediaAspectRatio,
} from "@markit/common.types";
import FullEventInfo from "../../components/FullEvent/FullEventInfo";
import FullEventHeader from "../../components/FullEvent/FullEventHeader";
import TopHeader from "../../components/TopHeader";
import CheckoutContainer from "../../components/FullEventSubComponents/CheckoutContainer";
import { useOnMount } from "../../hooks/useOnMount";
import { m, LazyMotion, domAnimation } from "framer-motion";
import { useTheme } from "../../hooks/useTheme";
import {
  getCanonicalMediaUrls,
  getVisibleCustomTickets,
} from "@markit/common.utils";
import MediaCarouselContainer from "../../components/Media/MediaCarouselContainer";
import MediaExpandedItem from "../../components/Media/MediaExpandedItem";

type FullEventProps = {
  event: Event;
  host: AccountData;
  cohostsList: AccountData[];
  posts: DiscussionPost[];
  address: string;
  tixId: string;
  ticketData: TicketV2 | undefined;
  ticketToRedeem: CustomTicketV2 | undefined;
  promoterId: string;
  promoCode?: PromoCode;
  isEventAtCapacity: boolean;
  userReferrer: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedTickets: CustomTicketV2[]) => void;
  eventFormQuestions: SavedFormQuestion[];
};

const FullEvent = memo(function FullEventFn(props: FullEventProps) {
  const {
    event,
    host,
    cohostsList,
    posts,
    address,
    tixId,
    ticketData,
    ticketToRedeem,
    promoterId, // promoterId
    promoCode, // promo
    isEventAtCapacity,
    userReferrer,
    selectedCustomTickets,
    setSelectedCustomTickets,
    eventFormQuestions,
  } = props;

  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [expandedImageUrl, setExpandedImageUrl] = useState<string>("");

  const refContainer = useRef<HTMLDivElement>(null);

  const { theme } = useTheme(event.theme);

  useOnMount(() => {
    // return back to main event screen if the ticket has already been redeemed or scanned
    if (ticketData && (ticketData.redeemedBy !== "" || ticketData.scanned)) {
      alert(
        "This ticket has already been used. You will need to purchase a ticket."
      );
      window.location.replace(`/e/${event.id}`);
    }
  });

  const visibleTickets = useMemo(
    () => getVisibleCustomTickets(event.customTickets, ticketToRedeem?.id),
    [event.customTickets, ticketToRedeem?.id]
  );

  const mediaUrls = useMemo(() => getCanonicalMediaUrls(event), [event]);

  const styles = {
    mediaFrame: {
      width: "100%",
      aspectRatio: event.mediaAspectRatio || MediaAspectRatio._1_1,
      borderRadius: 8,
      border: `1px solid ${theme.DividerColor.borderColor}`,
      overflow: "hidden",
      position: "relative" as const,
      flexShrink: 0,
    },
    mediaImage: {
      cursor: "pointer",
    },
  };

  return (
    <div>
      {showCheckoutForm ? (
        <LazyMotion features={domAnimation}>
          <m.div
            initial={{ y: "20px", opacity: 0, filter: "blur(2px)" }}
            animate={{ y: "0px", opacity: 1, filter: "blur(0px)" }}
            exit={{ y: "20px", opacity: 0, filter: "blur(2px)" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="HideScrollbar"
          >
            <CheckoutContainer
              event={event}
              host={host}
              tixId={tixId}
              ticketData={ticketData}
              ticketToRedeem={ticketToRedeem}
              promoterId={promoterId}
              promoCode={promoCode}
              userReferrer={userReferrer}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              eventFormQuestions={eventFormQuestions}
              isEventAtCapacity={isEventAtCapacity}
              setShowCheckoutForm={setShowCheckoutForm}
              theme={theme}
            />
          </m.div>
        </LazyMotion>
      ) : null}
      {/** Allow expanded image if there is just one media image */}
      {expandedImageUrl ? (
        <MediaExpandedItem
          mediaUrl={expandedImageUrl}
          onClose={() => setExpandedImageUrl("")}
          hardTheme={event.theme}
        />
      ) : null}
      <div
        style={showCheckoutForm ? { display: "none" } : { display: "block" }}
      >
        <div
          ref={refContainer}
          className="ColumnCenter"
          style={theme.SubComponentBG}
        >
          <TopHeader showRCA eventTheme={event.theme} />
          <div className="ColumnNormal" style={{ paddingInline: 14, gap: 30 }}>
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div style={styles.mediaFrame}>
                <MediaCarouselContainer
                  event={event}
                  selectedMediaIndex={selectedMediaIndex}
                  setSelectedMediaIndex={setSelectedMediaIndex}
                  imageStyle={styles.mediaImage}
                  onImageClick={() =>
                    setExpandedImageUrl(mediaUrls[selectedMediaIndex])
                  }
                />
              </div>
              <FullEventHeader
                event={event}
                host={host}
                cohostsList={cohostsList}
                address={address}
              />
            </div>
            <FullEventInfo
              event={event}
              host={host}
              cohostsList={cohostsList ?? []}
              posts={posts}
              address={address}
              visibleTickets={visibleTickets}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              promoCode={promoCode}
              redeemTicket={ticketData}
              setShowCheckoutForm={setShowCheckoutForm}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default FullEvent;
